import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
  useMemo,
} from 'react';
import {
  COLOR_MODE_KEY,
  QUERY_COLOR_MODE_KEY,
} from '@antimatterhq/ui/constants/color';
import {
  getLocalStorageValue,
  prefixStorageKey,
  setLocalStorageValue,
} from '@antimatterhq/ui/utils/localStorage';

export type ColorMode = 'light' | 'dark' | 'auto';

interface ColorModeContextType {
  colorMode: ColorMode;
  setColorMode: (mode: ColorMode) => void;
  isDarkMode: boolean;
  isLightMode: boolean;
  isAutoMode: boolean;
}

const ColorModeContext = createContext<ColorModeContextType | undefined>(
  undefined,
);

// Helper function to extract and validate color mode from URL query parameters
const getColorModeFromURL = (): ColorMode | null => {
  const params = new URLSearchParams(window.location.search);
  const queryColorMode = params.get(QUERY_COLOR_MODE_KEY) as ColorMode;
  return ['light', 'dark', 'auto'].includes(queryColorMode)
    ? queryColorMode
    : null;
};

export const ColorModeProvider = ({ children }: { children: ReactNode }) => {
  // Initialize color mode from URL query params, localStorage, or default to 'auto'
  const [colorMode, setColorMode] = useState<ColorMode>(() => {
    const queryColorMode = getColorModeFromURL();
    const savedMode = getLocalStorageValue(
      prefixStorageKey(COLOR_MODE_KEY),
    ) as ColorMode;

    // Set initial localStorage if it doesn't exist
    if (!savedMode) {
      setLocalStorageValue(
        prefixStorageKey(COLOR_MODE_KEY),
        queryColorMode || 'auto',
      );
    }

    const selectedMode = queryColorMode || savedMode || 'auto';
    // Immediately apply the selected mode
    document.documentElement.classList.toggle(
      'dark',
      selectedMode === 'dark' ||
        (selectedMode === 'auto' &&
          window.matchMedia('(prefers-color-scheme: dark)').matches),
    );

    return selectedMode;
  });

  // Track dark mode state based on document class
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    return document.documentElement.classList.contains('dark');
  });

  // Detect system color scheme preference
  const getSystemPreference = useCallback(() => {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  }, []);

  // Derived state for light and auto modes
  const isLightMode = !isDarkMode;
  const isAutoMode = colorMode === 'auto';

  // Apply the selected color mode to the document
  const applyColorMode = useCallback(
    (mode: ColorMode) => {
      if (mode === 'auto') {
        document.documentElement.classList.toggle(
          'dark',
          getSystemPreference(),
        );
      } else {
        document.documentElement.classList.toggle('dark', mode === 'dark');
      }
    },
    [getSystemPreference],
  );

  // Update color mode and persist to localStorage
  const setUserColorMode = useCallback(
    (mode: ColorMode) => {
      setColorMode(mode);
      applyColorMode(mode);
      setLocalStorageValue(prefixStorageKey(COLOR_MODE_KEY), mode);
    },
    [applyColorMode],
  );

  // Update the URL change handler to respect localStorage when URL param is removed
  useEffect(() => {
    const handleURLChange = () => {
      const queryColorMode = getColorModeFromURL();
      const savedMode = getLocalStorageValue(
        prefixStorageKey(COLOR_MODE_KEY),
      ) as ColorMode;

      if (queryColorMode) {
        setColorMode(queryColorMode);
        applyColorMode(queryColorMode);
        localStorage.setItem(COLOR_MODE_KEY, queryColorMode);
      } else if (savedMode) {
        setColorMode(savedMode);
        applyColorMode(savedMode);
      }
    };

    window.addEventListener('popstate', handleURLChange);
    handleURLChange(); // Initial URL check

    return () => {
      window.removeEventListener('popstate', handleURLChange);
    };
  }, [applyColorMode]);

  // Observer to track dark mode class changes
  useEffect(() => {
    const htmlElement = document.documentElement;
    const handleChange = () => {
      setIsDarkMode(htmlElement.classList.contains('dark'));
    };

    const observer = new MutationObserver(handleChange);
    observer.observe(htmlElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => observer.disconnect();
  }, []);

  // Listen for system color scheme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (colorMode === 'auto') {
        applyColorMode('auto');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [colorMode, applyColorMode]);

  const value = useMemo(
    () => ({
      colorMode,
      setColorMode: setUserColorMode,
      isDarkMode,
      isLightMode,
      isAutoMode,
    }),
    [colorMode, setUserColorMode, isDarkMode, isLightMode, isAutoMode],
  );

  return (
    <ColorModeContext.Provider value={value}>
      {children}
    </ColorModeContext.Provider>
  );
};

export const useColorMode = () => {
  const context = useContext(ColorModeContext);
  if (!context) {
    throw new Error('useColorMode must be used within a ColorModeProvider');
  }
  return context;
};
